import { NavLink, useLocation } from 'react-router-dom';
import {
    type MouseEvent,
    useEffect,
    useMemo,
    useState,
    type ReactNode,
} from 'react';
import clsx from 'clsx';
import { Button, Icon, OverflowTooltipLabel } from '@ics-portal/guardian';
import { type NavigationItem } from '@models/Navigation';
import { SubMenu } from '../SubMenu/SubMenu';
import s from '../../app-menu.module.scss';
import NavigationItemIcon from '../NavigationItemIcon/NavigationItemIcont';
import { findActiveChildren, shouldBeOpen } from './utils';

export interface LinkItemProps {
    item: NavigationItem;
    exact?: boolean;
    sidebarCollapsed?: boolean;
    isSubmenuItem?: boolean;
}

export function LinkItem({
    item,
    exact,
    sidebarCollapsed,
    isSubmenuItem,
}: LinkItemProps): ReactNode {
    const location = useLocation();

    const [isOpen, setIsOpen] = useState<boolean>(false);

    useEffect(() => {
        setIsOpen(shouldBeOpen(item, location.pathname));
    }, [location.pathname, item]);

    const handleToggleClick = (e: MouseEvent): void => {
        e.stopPropagation();
        e.preventDefault();
        setIsOpen(!isOpen);
    };

    const subMenuToggleClass = useMemo(
        () => clsx(s.subMenuToggle, isOpen ? s.subMenuToggleOpen : null),
        [isOpen],
    );

    const closedAndChildActive = useMemo(() => {
        const childIsActive = findActiveChildren(item, location.pathname);
        return childIsActive && !isOpen;
    }, [item, location.pathname, isOpen]);

    return (
        <li className={s.listItem}>
            <NavLink
                to={item.to}
                aria-label={item.label}
                className={s.link}
                end={exact}
            >
                {({ isActive }) => (
                    <>
                        {item.icon || (sidebarCollapsed && !item.icon) ? (
                            <div className={s.icon}>
                                <NavigationItemIcon
                                    item={item}
                                    isActive={isActive}
                                    sidebarIsCollapsed={sidebarCollapsed}
                                    isSubmenuItem={isSubmenuItem}
                                />
                            </div>
                        ) : null}
                        <span
                            className={`${s.label} ${sidebarCollapsed ? s.labelCollapsed : ''}`}
                        >
                            {!sidebarCollapsed ? (
                                <OverflowTooltipLabel
                                    label={item.label}
                                    className={s.labelContent}
                                />
                            ) : null}
                            {item.subMenu ? (
                                <Button
                                    className={`${subMenuToggleClass} ${isActive ? s.subMenuToggleActive : ''} ${closedAndChildActive ? s.subMenuToggleActive : ''}`}
                                    icon={<Icon name="keyboard_arrow_right" />}
                                    onClick={(e) => {
                                        handleToggleClick(e);
                                    }}
                                />
                            ) : null}
                        </span>
                    </>
                )}
            </NavLink>
            {item.subMenu && isOpen ? (
                <SubMenu
                    items={item.subMenu.items}
                    sidebarCollapsed={sidebarCollapsed}
                />
            ) : null}
        </li>
    );
}
